import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { libelleStatutDepuis } from '../../utils/statutAtelier';
import { getContrastColor } from '../../utils/planningColors';
import './RappelsRdvSimulation.css';

// ═══════════════════════════════════════════════════════════════════════════
// Rappels de RDV J-1 — l'écran qui montre ce qui PARTIRAIT demain, et qui
// permet de le faire partir soi-même, ligne par ligne.
//
// Le cron de 17h fait le travail ; cet écran existe pour qu'on puisse le lire
// AVANT de passer une concession en mode « actif ». Sans lui, la simulation ne
// vivait que dans les journaux du serveur — donc nulle part, en pratique.
//
// Depuis le 2026-09-29, étape intermédiaire : chaque ligne « Partirait » porte
// un bouton « Envoyer » qui expédie CE message, et seulement lui, après
// confirmation. Rien ne part tout seul depuis cette page : la liste reste une
// simulation, l'envoi est un geste explicite, ligne par ligne, tracé dans le
// dossier. Le bouton 👁️ ouvre le dossier pour contrôler avant d'envoyer.
// ═══════════════════════════════════════════════════════════════════════════

const ETATS = {
  simules: { pastille: 'part', libelle: 'Partirait' },
  envoyes: { pastille: 'part', libelle: 'Envoyé' },
  ignores: { pastille: 'ecarte', libelle: 'Écarté' },
  echecs: { pastille: 'echec', libelle: 'Échec' },
};

const MODES = {
  off: { libelle: 'Désactivé', classe: 'off' },
  simulation: { libelle: 'Simulation', classe: 'simu' },
  actif: { libelle: 'Actif — envoie réellement', classe: 'actif' },
};

export default function RappelsRdvSimulation({ onOuvrirDossier }) {
  const [rapport, setRapport] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  // Envois manuels de cette session : { [intervention_id]: { etat: 'envoi'|'envoye'|'echec', heure?, motif? } }
  const [envois, setEnvois] = useState({});

  const charger = useCallback(() => {
    setChargement(true);
    api.post('/planning/rappels-rdv/simuler')
      .then(r => { setRapport(r.data); setErreur(''); setEnvois({}); })
      .catch(e => setErreur(e.response?.data?.error || 'Impossible de lancer la simulation.'))
      .finally(() => setChargement(false));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const envoyer = async (l) => {
    const ok = window.confirm(
      `Envoyer ce SMS maintenant ?\n\nÀ : ${l.client} — ${l.tel}\n\n${l.message}\n\n`
      + 'Le message part immédiatement chez le client. Un dossier rappelé ne le sera pas une seconde fois par le cron.'
    );
    if (!ok) return;
    setEnvois(prev => ({ ...prev, [l.intervention_id]: { etat: 'envoi' } }));
    try {
      await api.post(`/planning/rappels-rdv/${l.intervention_id}/envoyer`);
      const heure = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      setEnvois(prev => ({ ...prev, [l.intervention_id]: { etat: 'envoye', heure } }));
    } catch (e) {
      setEnvois(prev => ({ ...prev, [l.intervention_id]: { etat: 'echec', motif: e.response?.data?.error || "Échec de l'envoi" } }));
    }
  };

  if (chargement && !rapport) return <div className="rr-etat">Simulation en cours…</div>;
  if (erreur) return <div className="rr-etat rr-erreur">{erreur}</div>;

  const jour = rapport?.date
    ? new Date(rapport.date + 'T12:00:00').toLocaleDateString('fr-FR',
        { weekday: 'long', day: 'numeric', month: 'long' })
    : '—';
  const total = (rapport?.simules || 0) + (rapport?.envoyes || 0);
  const nbEnvoyesIci = Object.values(envois).filter(e => e.etat === 'envoye').length;

  return (
    <div className="rr-page">
      <div className="rr-chapeau">
        <div>
          <h2>📱 Rappels de RDV</h2>
          <p className="rr-sous">
            Ce qui partirait <strong>aujourd'hui à 17h00</strong> pour les rendez-vous du <strong>{jour}</strong>.
            Rien ne part tout seul depuis cette page : le bouton <strong>Envoyer</strong> d'une ligne expédie ce message,
            et seulement lui, après confirmation. Un dossier envoyé ici ne sera pas rappelé une seconde fois à 17h.
          </p>
        </div>
        <button type="button" className="rr-relancer" onClick={charger} disabled={chargement}>
          {chargement ? 'Simulation…' : '↻ Relancer'}
        </button>
      </div>

      <div className="rr-compteurs">
        <span className="rr-cpt"><strong>{total}</strong> message{total > 1 ? 's' : ''}</span>
        {nbEnvoyesIci > 0 && <span className="rr-cpt rr-bon">{nbEnvoyesIci} envoyé{nbEnvoyesIci > 1 ? 's' : ''} depuis cette page</span>}
        {rapport?.ignores > 0 && <span className="rr-cpt rr-doux">{rapport.ignores} écarté{rapport.ignores > 1 ? 's' : ''}</span>}
        {rapport?.echecs > 0 && <span className="rr-cpt rr-mauvais">{rapport.echecs} échec{rapport.echecs > 1 ? 's' : ''}</span>}
      </div>

      {!rapport?.concessions?.length && (
        <div className="rr-vide">
          Aucune concession n'est en mode simulation ou actif, ou aucun rendez-vous n'est éligible demain.
          <br />Le réglage se trouve dans <strong>Administration ▸ Concessions ▸ Rappel de RDV la veille</strong>.
        </div>
      )}

      {(rapport?.concessions || []).map(c => (
        <div className="rr-bloc" key={c.concession_id}>
          <div className="rr-bloc-tete">
            <h3>{c.concession}</h3>
            <span className={`rr-mode ${MODES[c.mode]?.classe || 'off'}`}>{MODES[c.mode]?.libelle || c.mode}</span>
            <span className="rr-bloc-cpt">
              {c.simules + c.envoyes} message{c.simules + c.envoyes > 1 ? 's' : ''}
              {c.ignores > 0 && ` · ${c.ignores} écarté${c.ignores > 1 ? 's' : ''}`}
              {c.echecs > 0 && ` · ${c.echecs} échec${c.echecs > 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="rr-table-scroll">
            <table className="rr-table">
              <thead>
                <tr><th>Client</th><th>Téléphone</th><th>RDV</th><th>Statut du dossier</th><th>Message qui partirait</th><th className="rr-th-actions">Actions</th></tr>
              </thead>
              <tbody>
                {c.lignes.map((l, i) => {
                  const envoi = envois[l.intervention_id];
                  const etat = envoi?.etat === 'envoye' ? 'envoyes' : l.etat;
                  const partirait = l.etat === 'simules';
                  return (
                    <tr key={l.intervention_id || i} className={etat === 'simules' || etat === 'envoyes' ? '' : 'rr-ligne-ecartee'}>
                      <td>
                        <span className={`rr-pastille ${ETATS[etat]?.pastille}`}>{ETATS[etat]?.libelle}</span>
                        {l.client}
                        {l.immat && <span className="rr-immat">{l.immat}</span>}
                      </td>
                      <td className="rr-tel">{l.tel || '—'}</td>
                      <td className="rr-tel">{l.heure || '—'}</td>
                      {/* Statut atelier + depuis quand (« Réceptionné depuis
                          aujourd'hui ») : repère les véhicules déjà chez nous. */}
                      <td className="rr-statut">
                        {l.statut ? (
                          <span className="rr-statut-pastille" style={l.statut.couleur ? {
                            background: l.statut.couleur,
                            color: l.statut.text_color || getContrastColor(l.statut.couleur),
                          } : undefined}>
                            {l.statut.nom} {libelleStatutDepuis(l.statut.nom, l.statut.depuis, l.statut.estime)}
                          </span>
                        ) : <span className="rr-statut-vide">—</span>}
                      </td>
                      {/* Le motif d'exclusion prend la place du message : c'est
                          l'information utile sur cette ligne. */}
                      <td className={l.message ? 'rr-message' : 'rr-motif'}>
                        {l.message || l.motif}
                        {envoi?.etat === 'envoye' && <div className="rr-envoi-ok">✓ Envoyé à {envoi.heure}</div>}
                        {envoi?.etat === 'echec' && <div className="rr-envoi-ko">✗ {envoi.motif}</div>}
                      </td>
                      <td className="rr-actions">
                        <div className="action-buttons">
                          {l.intervention_id && onOuvrirDossier && (
                            <button type="button" className="btn-icon" title="Ouvrir le dossier"
                              onClick={() => onOuvrirDossier(l.intervention_id)}>👁️</button>
                          )}
                          {partirait && envoi?.etat !== 'envoye' && (
                            <button type="button" className="rr-envoyer"
                              disabled={envoi?.etat === 'envoi'}
                              title="Envoyer ce SMS maintenant, après confirmation"
                              onClick={() => envoyer(l)}>
                              {envoi?.etat === 'envoi' ? '⏳' : '📤 Envoyer'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
